import { DecimalPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EmbeddedViewRef,
  Injector,
  OnChanges,
  TemplateRef,
  ViewContainerRef,
  afterNextRender,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as d3 from 'd3';

import { ResizeObserverService } from '@core/services/resize-observer.service';

import { SITE_TIME_ZONE } from '@shared/context/site-time-zone.context';
import { D3ChartSvg, D3ChartSvgg, XAxisData, YAxisData } from '@shared/types/common-graph.types';

import {
  ChartConfig,
  ChartCursorTooltipTemplateContext,
  ChartDataPointTemplateContext,
  ChartMargin,
  ChartPointerPositionConfig,
  ChartType,
  ChartXAxisTickTemplateContext,
} from './chart.view-model';
import { BarChartService } from './services/bar-chart.service';
import { GridService } from './services/grid.service';
import { LineChartService } from './services/line-chart.service';
import { ChartData, CursorTooltipRenderer, LineTooltipService } from './services/line-tooltip.service';
import { NowLineService } from './services/now-line.service';
import { PointerPositionLabelRenderer, PointerPositionService } from './services/pointer-position.service';
import { XAxisService } from './services/x-axis.service';
import { YAxisService } from './services/y-axis.service';

@Component({
  selector: 'app-chart',
  standalone: true,
  providers: [
    BarChartService,
    LineChartService,
    GridService,
    LineTooltipService,
    PointerPositionService,
    XAxisService,
    YAxisService,
    DecimalPipe,
    NowLineService,
  ],
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent<Data extends object> implements OnChanges, AfterViewInit {
  readonly #yAxisService = inject<YAxisService<Data>>(YAxisService);
  readonly #xAxisService = inject<XAxisService<Data>>(XAxisService);
  readonly #gridService = inject(GridService);
  readonly #barChartService = inject<BarChartService<Data>>(BarChartService);
  readonly #lineChartService = inject<LineChartService<Data>>(LineChartService);
  readonly #pointerPositionService = inject<PointerPositionService<Data>>(PointerPositionService);
  readonly #lineTooltipService = inject<LineTooltipService<Data>>(LineTooltipService);
  readonly #nowLineService = inject(NowLineService);
  readonly #resizeObserverService = inject(ResizeObserverService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #injector = inject(Injector);
  readonly #siteTimeZone = inject(SITE_TIME_ZONE);
  public config = input.required<ChartConfig<Data>>();

  public container = viewChild.required<ElementRef<HTMLDivElement>>('container');
  public overlay = viewChild.required('overlay', { read: ViewContainerRef });
  public pointerPositionXAxisLabelTemplate =
    viewChild.required<TemplateRef<PointerPositionXAxisLabelTemplateContext>>('pointerPositionXAxisLabel');
  private readonly defaultMargin: ChartMargin = { top: 48, right: 20, bottom: 18, left: 22 };
  private readonly defaultPointerPositionLabelMinWidth = 50;
  private margin!: ChartMargin;
  private readonly defaultYAxisAndLinesGap = 4;
  private readonly gapAboveXAxis = 0;
  private readonly pointerPositionLabelYOffset = 5;
  private svg!: D3ChartSvg;
  private chartsArea!: D3ChartSvgg;
  private linesContainer!: D3ChartSvgg;
  private fullWidth!: number;
  private fullHeight!: number;
  private availableChartsAreaWidth!: number;
  private availableChartHeight!: number;
  private yRightAxisWidth?: number;
  private yLeftAxisWidth?: number;
  private yRightAxisG?: D3ChartSvgg;
  private yLeftAxisG?: D3ChartSvgg;
  private xScale!: XAxisData;
  private yRightScale?: YAxisData;
  private yLeftScale?: YAxisData;
  private cursorTooltipView?: EmbeddedViewRef<ChartCursorTooltipTemplateContext<Data>>;
  private cursorTooltipElement?: HTMLElement;
  private pointerPositionXAxisLabelView?: EmbeddedViewRef<PointerPositionXAxisLabelTemplateContext>;
  private pointerPositionXAxisLabelElement?: HTMLElement;

  public ngOnChanges(): void {
    if (!this.svg) {
      return;
    }

    afterNextRender(() => this.generateChart(), { injector: this.#injector });
  }

  public ngAfterViewInit(): void {
    this.generateChart();
    this.regenerateOnResize();
  }

  private generateChart() {
    const config = this.config();
    this.resetEventListeners();
    this.setDimensions();
    this.createSvg();

    if (this.isEmptyData()) {
      return;
    }

    this.createChartsArea();
    if (config.leftYAxis) {
      this.createLeftYAxis();
    }

    this.createLinesContainer();

    if (config.rightYAxis) {
      this.createRightYAxis();
    }
    this.updateDimensionsBasedOnYAxisWidth();
    this.setLinesContainerWidth();
    this.createXAxis();
    this.updateAvailableHeightBasedOnXAxisHeight();
    this.redrawYAxisesBasedOnNewAvailableChartHeight();

    if (!config.grid?.hidden) {
      this.createGrid();
    }

    this.createGradients();
    this.createCharts();
    this.renderDataPointExtensions();
    this.renderXAxisTickTemplate();
    this.generateNowLine();
    this.generatePointerPositionVerticalLine();
    this.generateTooltipsForLines();
    this.generateDataPointClickHandler();
  }

  private isEmptyData(): boolean {
    const config = this.config();

    return !config.data || config.data.length === 0;
  }

  private regenerateOnResize(): void {
    this.#resizeObserverService
      .observe$(this.container().nativeElement)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.generateChart();
      });
  }

  private setDimensions(): void {
    const config = this.config();
    const container = this.container().nativeElement;
    const { width: fullWidth, height: fullHeight } = container.getBoundingClientRect();
    this.fullWidth = fullWidth;
    this.fullHeight = fullHeight;

    this.margin = {
      ...this.defaultMargin,
      ...config.margin,
    };

    this.availableChartsAreaWidth = fullWidth - this.margin.left - this.margin.right;
    this.availableChartHeight = fullHeight - this.margin.top - this.margin.bottom;
  }

  private createSvg(): void {
    this.overlay().clear();
    this.cursorTooltipView = undefined;
    this.cursorTooltipElement = undefined;
    this.pointerPositionXAxisLabelView = undefined;
    this.pointerPositionXAxisLabelElement = undefined;
    this.svg?.remove();
    this.svg = d3.select(this.container().nativeElement).append('svg').attr('width', this.fullWidth).attr('height', this.fullHeight);
    this.svg.append('defs');
  }

  private createChartsArea(): void {
    this.chartsArea = this.svg.append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);
  }

  private createLeftYAxis(): void {
    const config = this.config();

    if (!config.leftYAxis) {
      return;
    }

    const result = this.#yAxisService.createYAxis(
      this.chartsArea,
      this.availableChartHeight,
      this.margin.left,
      config.data,
      config.leftYAxis!
    );
    this.yLeftScale = result.yScale;
    this.yLeftAxisWidth = result.yAxisWidth;
    this.yLeftAxisG = result.yAxisG;
  }

  private createRightYAxis(): void {
    const config = this.config();

    if (!config.rightYAxis) {
      return;
    }

    const result = this.#yAxisService.createYAxis(
      this.chartsArea,
      this.availableChartHeight,
      this.margin.left + (this.yLeftAxisWidth ?? 0) + this.availableChartsAreaWidth,
      config.data,
      config.rightYAxis!,
      true
    );
    this.yRightScale = result.yScale;
    this.yRightAxisWidth = result.yAxisWidth;
    this.yRightAxisG = result.yAxisG;
  }

  private updateDimensionsBasedOnYAxisWidth(): void {
    this.availableChartsAreaWidth -=
      (this.yRightAxisWidth ? this.yRightAxisWidth + (this.config().rightYAxis?.axisAndLinesGap ?? this.defaultYAxisAndLinesGap) : 0) +
      (this.yLeftAxisWidth ? this.yLeftAxisWidth + (this.config().leftYAxis?.axisAndLinesGap ?? this.defaultYAxisAndLinesGap) : 0);
  }

  private createLinesContainer(): void {
    this.linesContainer = this.chartsArea.insert('g', ':first-child').attr('height', this.availableChartHeight);
  }

  private setLinesContainerWidth(): void {
    this.linesContainer.attr('width', this.availableChartsAreaWidth).attr(
      'transform',
      // eslint-disable-next-line max-len
      `translate(${this.yLeftAxisWidth ? this.yLeftAxisWidth + (this.config().leftYAxis?.axisAndLinesGap ?? this.defaultYAxisAndLinesGap) : 0}, 0)`
    );
  }

  private createXAxis(): void {
    const config = this.config();

    this.xScale = this.#xAxisService.createXAxis(
      config.data,
      config.xAxis,
      this.linesContainer,
      this.availableChartsAreaWidth,
      this.availableChartHeight,
      this.#siteTimeZone()
    );
  }

  private updateAvailableHeightBasedOnXAxisHeight(): void {
    this.availableChartHeight -= this.xScale.height + this.gapAboveXAxis;
  }

  private redrawYAxisesBasedOnNewAvailableChartHeight(): void {
    this.yRightAxisG?.remove();
    this.createRightYAxis();

    this.yLeftAxisG?.remove();
    this.createLeftYAxis();
  }

  private createGrid(): void {
    this.#gridService.createGrid(
      this.linesContainer,
      this.availableChartsAreaWidth,
      this.yLeftScale ?? this.yRightScale!,
      this.config().grid
    );
  }

  private createGradients(): void {
    const config = this.config();

    config.charts.forEach((chart) => {
      if (chart.type !== ChartType.Line || !chart.gradientConfig) {
        return;
      }

      this.#lineChartService.drawGradientArea(
        this.svg,
        config.data,
        this.availableChartsAreaWidth,
        this.linesContainer,
        this.xScale.scale,
        chart.rightYAxis ? this.yRightScale!.scale : this.yLeftScale!.scale,
        chart,
        config.xAxis
      );
    });
  }

  private createCharts(): void {
    const config = this.config();
    config.charts.forEach((chart) => {
      switch (chart.type) {
        case ChartType.BackgroundRect: {
          this.#barChartService.drawBackgroundRects(
            config.data,
            this.linesContainer,
            this.xScale,
            this.yLeftScale?.scale || this.yRightScale!.scale,
            chart,
            config.xAxis
          );
          break;
        }
        case ChartType.Bar: {
          this.#barChartService.drawBarChart(
            config.data,
            this.svg,
            this.linesContainer,
            this.xScale,
            chart.rightYAxis ? this.yRightScale!.scale : this.yLeftScale!.scale,
            chart,
            config.xAxis
          );
          break;
        }
        case ChartType.Line: {
          this.#lineChartService.drawLineChart(
            config.data,
            this.linesContainer,
            this.availableChartsAreaWidth,
            this.xScale.scale,
            chart.rightYAxis ? this.yRightScale!.scale : this.yLeftScale!.scale,
            chart,
            config.xAxis
          );
          break;
        }
        case ChartType.StackedBar: {
          this.#barChartService.drawStackedBarChart(
            config.data,
            this.svg,
            this.linesContainer,
            this.xScale,
            chart.rightYAxis ? this.yRightScale!.scale : this.yLeftScale!.scale,
            chart,
            config.xAxis
          );
          break;
        }
        case ChartType.GroupedBar: {
          this.#barChartService.drawGroupedBarChart(
            config.data,
            this.svg,
            this.linesContainer,
            this.xScale,
            chart.rightYAxis ? this.yRightScale!.scale : this.yLeftScale!.scale,
            chart,
            config.xAxis
          );
          break;
        }
      }
    });
  }

  private generateNowLine(): void {
    const config = this.config();
    if (!config.nowLineConfig) {
      return;
    }

    this.#nowLineService.generateNowLine(
      this.linesContainer,
      config.nowLineConfig,
      this.availableChartHeight,
      this.xScale.scale,
      this.margin.top - 1,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      this.margin.bottom + 9
    );
  }

  private generatePointerPositionVerticalLine(): void {
    const config = this.config();

    this.#pointerPositionService.generatePointerPositionVerticalLine(
      config.data,
      this.svg,
      this.linesContainer,
      this.availableChartsAreaWidth,
      this.availableChartHeight,
      this.xScale.scale,
      config.xAxis,
      config.pointerPosition,
      this.createPointerPositionLabelRenderer(config.pointerPosition),
      this.#siteTimeZone()
    );
  }

  private generateTooltipsForLines(): void {
    const config = this.config();

    this.#lineTooltipService.generateTooltipsForLines(
      this.svg,
      this.linesContainer,
      this.availableChartsAreaWidth,
      this.availableChartHeight,
      this.xScale.scale,
      config.xAxis,
      config.charts.map((chart): ChartData<Data> => {
        return {
          chart,
          yScale: chart.rightYAxis ? this.yRightScale!.scale : this.yLeftScale!.scale,
        };
      }),
      config.data,
      this.createCursorTooltipRenderer()
    );
  }

  private generateDataPointClickHandler(): void {
    const onDataPointClick = this.config().dataPointClick;
    if (!onDataPointClick) {
      return;
    }

    this.svg.on('click', (event: MouseEvent) => {
      const [x] = d3.pointer(event, this.linesContainer.node());
      const nearestDataPoint = this.config().data.reduce(
        (closest, data, index) => {
          const value = data[this.config().xAxis.dataKey] as number | { toMillis: () => number };
          const dataX = this.xScale.scale(typeof value === 'number' ? value : value.toMillis());

          return Math.abs(dataX - x) < closest.distance ? { data, index, distance: Math.abs(dataX - x) } : closest;
        },
        { data: this.config().data[0], index: 0, distance: Number.POSITIVE_INFINITY }
      );

      onDataPointClick(nearestDataPoint.data, nearestDataPoint.index);
    });
  }

  private renderDataPointExtensions(): void {
    const config = this.config();
    const dataPointTemplates = config.dataPointTemplates ?? [];
    if (dataPointTemplates.length === 0) {
      return;
    }

    config.data.forEach((data, index) => {
      const xValue = data[config.xAxis.dataKey] as number | { toMillis: () => number };
      const x = this.xScale.scale(typeof xValue === 'number' ? xValue : xValue.toMillis());
      const context = { data, index, x, y: 0 };

      dataPointTemplates.forEach((templateConfig) =>
        this.renderDataPointTemplate(templateConfig.template, templateConfig.position, context)
      );
    });
  }

  private renderXAxisTickTemplate(): void {
    const template = this.config().xAxisTickTemplate?.template;
    if (!template) {
      return;
    }

    const vertical = this.xScale.verticalTexts ?? false;
    this.config().data.forEach((data, index) => {
      const xValue = data[this.config().xAxis.dataKey] as number | { toMillis: () => number };
      const x = this.xScale.scale(typeof xValue === 'number' ? xValue : xValue.toMillis());
      const context: ChartXAxisTickTemplateContext<Data> = {
        $implicit: data,
        data,
        index,
        x,
        y: this.availableChartHeight,
        vertical,
      };
      const view = this.overlay().createEmbeddedView(template, context);
      const element = this.getViewRootElement(view);
      if (element) {
        this.positionOverlayElement(element, context.x, context.y, true);
      }
    });
  }

  private renderDataPointTemplate(
    template: TemplateRef<ChartDataPointTemplateContext<Data>>,
    position: ((context: ChartDataPointTemplateContext<Data>) => { x: number; y: number }) | undefined,
    context: { data: Data; index: number; x: number; y: number }
  ): void {
    const templateContext: ChartDataPointTemplateContext<Data> = { $implicit: context.data, ...context };
    const view = this.overlay().createEmbeddedView(template, templateContext);
    const element = this.getViewRootElement(view);
    if (!element) {
      return;
    }

    const point = position?.(templateContext) ?? templateContext;
    this.positionOverlayElement(element, point.x, point.y, true);
  }

  private createCursorTooltipRenderer(): CursorTooltipRenderer<Data> | undefined {
    const template = this.config().cursorTooltipTemplate?.template;
    if (!template) {
      return undefined;
    }

    const context: ChartCursorTooltipTemplateContext<Data> = {
      $implicit: this.config().data[0],
      data: this.config().data[0],
      index: 0,
      x: 0,
    };
    this.cursorTooltipView = this.overlay().createEmbeddedView(template, context);
    this.cursorTooltipElement = this.getViewRootElement(this.cursorTooltipView);
    this.cursorTooltipElement?.style.setProperty('display', 'none');

    return {
      show: (data, index, x) => {
        if (!this.cursorTooltipView || !this.cursorTooltipElement) {
          return 0;
        }

        Object.assign(this.cursorTooltipView.context, { $implicit: data, data, index, x });
        this.cursorTooltipElement.style.removeProperty('display');
        this.cursorTooltipView.detectChanges();
        this.positionOverlayElement(this.cursorTooltipElement, x, 0, true);

        return this.cursorTooltipElement.getBoundingClientRect().height;
      },
      hide: () => this.cursorTooltipElement?.style.setProperty('display', 'none'),
    };
  }

  private createPointerPositionLabelRenderer(config?: ChartPointerPositionConfig): PointerPositionLabelRenderer {
    const context: PointerPositionXAxisLabelTemplateContext = {
      backgroundColorClass: this.getHtmlColorClass(config?.bgFillColorClass, 'bg', 'bg-foreground-primary-100'),
      minWidth: config?.minWidth ?? this.defaultPointerPositionLabelMinWidth,
      text: '',
      textColorClass: this.getHtmlColorClass(config?.textColorFillClass, 'text', 'text-foreground-secondary-100'),
    };
    this.pointerPositionXAxisLabelView = this.overlay().createEmbeddedView(this.pointerPositionXAxisLabelTemplate(), context);
    this.pointerPositionXAxisLabelElement = this.getViewRootElement(this.pointerPositionXAxisLabelView);
    this.pointerPositionXAxisLabelElement?.style.setProperty('display', 'none');

    return {
      show: (text, x) => {
        if (!this.pointerPositionXAxisLabelView || !this.pointerPositionXAxisLabelElement) {
          return;
        }

        Object.assign(this.pointerPositionXAxisLabelView.context, { text });
        this.pointerPositionXAxisLabelElement.style.removeProperty('display');
        this.pointerPositionXAxisLabelView.detectChanges();
        this.positionOverlayElement(
          this.pointerPositionXAxisLabelElement,
          x,
          this.availableChartHeight + this.pointerPositionLabelYOffset,
          true
        );
      },
      hide: () => this.pointerPositionXAxisLabelElement?.style.setProperty('display', 'none'),
    };
  }

  private getHtmlColorClass(fillColorClass: string | undefined, htmlPrefix: 'bg' | 'text', defaultClass: string): string {
    return fillColorClass ? fillColorClass.replace(/^fill-/, `${htmlPrefix}-`) : defaultClass;
  }

  private getViewRootElement(view: EmbeddedViewRef<unknown>): HTMLElement | undefined {
    return view.rootNodes.find((node): node is HTMLElement => node instanceof HTMLElement);
  }

  private positionOverlayElement(element: HTMLElement, x: number, y: number, centered: boolean): void {
    element.style.setProperty('position', 'absolute');
    element.style.setProperty('left', `${this.getChartOriginX() + x}px`);
    element.style.setProperty('top', `${this.margin.top + y}px`);
    element.style.setProperty('transform', centered ? 'translateX(-50%)' : 'none');
  }

  private getChartOriginX(): number {
    return (
      this.margin.left +
      (this.yLeftAxisWidth ? this.yLeftAxisWidth + (this.config().leftYAxis?.axisAndLinesGap ?? this.defaultYAxisAndLinesGap) : 0)
    );
  }

  private resetEventListeners(): void {
    this.#lineTooltipService.resetListeners();
  }
}

interface PointerPositionXAxisLabelTemplateContext {
  backgroundColorClass: string;
  minWidth: number;
  text: string;
  textColorClass: string;
}
